<template>
  <el-main>
    <content-placeholders
      v-if="isLoading"
      class="w-25 pb-3"
    >
      <content-placeholders-heading />
    </content-placeholders>

    <Filters
      v-else
      :date-range.sync="dateRange"
      class="pb-3"
    />

    <el-row :gutter="16">
      <el-col
        :span="24"
      >
        <!-- Statistics -->
        <el-row class="pb-2">
          <Statistics :statistics="dashboards.statistics" />
        </el-row>

        <!-- Destination Insights & Cost Analysis -->
        <el-row
          :gutter="8"
          class="pb-2"
        >
          <el-col :span="12">
            <el-card
              header="Destination Insights"
              shadow="never"
            >
              <div
                v-if="isLoading"
                v-loading="true"
                class="p-5"
              />

              <DestinationInsights
                v-else
                :destination-insights="dashboards.destination_insights"
              />
            </el-card>
          </el-col>

          <el-col :span="12">
            <el-card
              header="Cost Analysis"
              shadow="never"
            >
              <div
                v-if="isLoading"
                v-loading="true"
                class="p-5"
              />

              <CostAnalysis
                v-else
                :cost-analysis="dashboards.cost_analysis"
              />
            </el-card>
          </el-col>
        </el-row>

        <!-- Consignment Trends -->
        <el-row class="pb-2">
          <el-card
            header="Consignment Trends"
            shadow="never"
          >
            <div
              v-if="isLoading"
              v-loading="true"
              class="p-5"
            />

            <ConsignmentTrends
              v-else
              :consignment-trends="dashboards.consignment_trends"
            />
          </el-card>
        </el-row>

        <!-- Receiver Insights, Carrier Analysis -->
        <el-row
          :gutter="8"
          class="pb-2"
        >
          <el-col :span="12">
            <el-tabs type="border-card">
              <el-tab-pane :disabled="isLoading">
                <span slot="label">
                  <b>Top 10 Receivers</b>
                </span>

                <content-placeholders
                  v-if="isLoading"
                  :rounded="true"
                >
                  <content-placeholders-heading />
                  <content-placeholders-img />
                </content-placeholders>

                <div
                  v-else
                  class="text-center font-weight-bold text-capitalize"
                >
                  <div class="mt-2 mb-3">
                    Top 10 receivers by
                    <el-dropdown @command="setTopReceiversProperty">
                      <span>
                        {{ topReceiversProperty }}<i class="el-icon-arrow-down el-icon--right" />
                      </span>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item
                          command="weight"
                          :disabled="topReceiversProperty === 'weight'"
                        >
                          Weight
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="volume"
                          :disabled="topReceiversProperty === 'volume'"
                        >
                          Volume
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="consignments"
                          :disabled="topReceiversProperty === 'consignments'"
                        >
                          Consignments
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="items"
                          :disabled="topReceiversProperty === 'items'"
                        >
                          Items
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="cost"
                          :disabled="topReceiversProperty === 'cost'"
                        >
                          Cost
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>

                  <TopReceivers
                    :receivers="dashboards.top_receivers"
                    :property="topReceiversProperty"
                  />
                </div>
              </el-tab-pane>

              <el-tab-pane :disabled="isLoading">
                <span slot="label">
                  <b>All Receivers</b>
                </span>

                <content-placeholders
                  v-if="isLoading"
                  :rounded="true"
                >
                  <content-placeholders-heading />
                  <content-placeholders-img />
                </content-placeholders>

                <AllReceivers
                  v-else
                  :receivers="dashboards.top_receivers"
                />
              </el-tab-pane>
            </el-tabs>
          </el-col>

          <el-col :span="12">
            <el-tabs type="border-card">
              <el-tab-pane :disabled="isLoading">
                <span slot="label">
                  <b>Carrier Breakdown</b>
                </span>

                <content-placeholders
                  v-if="isLoading"
                  :rounded="true"
                >
                  <content-placeholders-heading />
                  <content-placeholders-img />
                  <content-placeholders-text :lines="2" />
                </content-placeholders>

                <div
                  v-else
                  class="text-center font-weight-bold text-capitalize"
                >
                  <div class="mt-2 mb-3">
                    {{ carrierAnalysisGraphTitle }}
                    <el-dropdown @command="setCarrierBreakdownProperty">
                      <span>
                        {{ carrierBreakdownProperty }}<i class="el-icon-arrow-down el-icon--right" />
                      </span>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item
                          command="weight"
                          :disabled="carrierBreakdownProperty === 'weight'"
                        >
                          Weight
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="volume"
                          :disabled="carrierBreakdownProperty === 'volume'"
                        >
                          Volume
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="consignments"
                          :disabled="carrierBreakdownProperty === 'consignments'"
                        >
                          Consignments
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="items"
                          :disabled="carrierBreakdownProperty === 'items'"
                        >
                          Items
                        </el-dropdown-item>

                        <el-dropdown-item
                          command="cost"
                          :disabled="carrierBreakdownProperty === 'cost'"
                        >
                          Cost
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>

                  <CarrierBreakdown
                    :carriers="dashboards.top_carriers"
                    :property="carrierBreakdownProperty"
                  />
                </div>
              </el-tab-pane>

              <el-tab-pane :disabled="isLoading">
                <span slot="label">
                  <b>Lane Summary</b>
                </span>

                <content-placeholders
                  v-if="isLoading"
                  :rounded="true"
                >
                  <content-placeholders-heading />
                  <content-placeholders-img />
                  <content-placeholders-text :lines="2" />
                </content-placeholders>

                <LaneSummary
                  v-else
                  :lanes="dashboards.lanes"
                />
              </el-tab-pane>
            </el-tabs>
          </el-col>
        </el-row>
      </el-col>

      <el-col
        v-if="showInProgressEventCharts"
        :span="6"
      >
        <el-row class="pb-2">
          <el-card
            header="On For Delivery"
            shadow="never"
          >
            <content-placeholders v-if="isLoading">
              <content-placeholders-text :lines="6" />
            </content-placeholders>

            <OnForDelivery
              v-else
              :on-for-delivery="dashboards.on_for_delivery"
            />
          </el-card>
        </el-row>

        <el-row class="pb-2">
          <el-card
            header="Recently Delivered"
            shadow="never"
          >
            <content-placeholders v-if="isLoading">
              <content-placeholders-text :lines="4" />
            </content-placeholders>

            <RecentlyDelivered
              v-else
              :recently-delivered="dashboards.recently_delivered"
            />
          </el-card>
        </el-row>
      </el-col>
    </el-row>
  </el-main>
</template>

<script>
import Dashboard from '@/plugins/sdk/resources/Dashboard'
import { ImmortalDB as Database } from 'immortal-db'
import Filters from '@/components/Dashboard/Filters'
import Statistics from '@/components/Dashboard/Statistics'
import DestinationInsights from '@/components/Dashboard/DestinationInsights'
import CostAnalysis from '@/components/Dashboard/CostAnalysis'
import CarrierBreakdown from '@/components/Dashboard/CarrierBreakdown'
import AllReceivers from '@/components/Dashboard/AllReceivers'
import TopReceivers from '@/components/Dashboard/TopReceivers'
import LaneSummary from '@/components/Dashboard/LaneSummary'
import ConsignmentTrends from '@/components/Dashboard/ConsignmentTrends'
import OnForDelivery from '@/components/Dashboard/OnForDelivery'
import RecentlyDelivered from '@/components/Dashboard/RecentlyDelivered'

const DEFAULT_DATE_RANGE = 'month'
const DEFAULT_TOP_RECEIVERS_PROPERTY = 'cost'
const DEFAULT_CARRIER_BREAKDOWN_PROPERTY = 'cost'

function sleep (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
  components: {
    Filters,
    Statistics,
    DestinationInsights,
    CostAnalysis,
    CarrierBreakdown,
    AllReceivers,
    TopReceivers,
    LaneSummary,
    ConsignmentTrends,
    OnForDelivery,
    RecentlyDelivered
  },
  data () {
    return {
      showInProgressEventCharts: false,
      isLoadingDashboardData: true,
      dateRange: null,
      dashboards: Dashboard.fields,
      topReceiversProperty: null,
      carrierBreakdownProperty: null
    }
  },
  computed: {
    isLoading () {
      return this.isLoadingDashboardData
    },
    dateFrom () {
      return this.$moment().startOf(this.dateRange)
    },
    dateTo () {
      return this.$moment().endOf('day')
    },
    previousDateFrom () {
      const dateFrom = this.dateFrom

      return dateFrom.clone().subtract(1, this.dateRange).startOf('day')
    },
    previousDateTo () {
      const dateTo = this.dateTo

      return dateTo.clone().subtract(1, this.dateRange).endOf('day')
    },
    carrierAnalysisGraphTitle () {
      const dashboards = this.dashboards

      if (dashboards.top_carriers.length > 5) {
        return 'Top 5 carriers by'
      } else {
        return 'Carrier breakdown by'
      }
    }
  },
  watch: {
    dateRange: async function (value) {
      await Database.set('dashboard-date-range', value)

      this.loadDashboardData()
    },
    topReceiversProperty: async function (value) {
      await Database.set('dashboard-receivers-by', value)
    },
    carrierBreakdownProperty: async function (value) {
      await Database.set('dashboard-carriers-by', value)
    }
  },
  async mounted () {
    // Chartjs will crash if data is returned too quickly after mount.
    // This is a "temporary" fix. hahaha.
    await sleep(3000)

    this.dateRange = (await Database.get('dashboard-date-range')) || DEFAULT_DATE_RANGE
    this.topReceiversProperty = (await Database.get('dashboard-receivers-by')) || DEFAULT_TOP_RECEIVERS_PROPERTY
    this.carrierBreakdownProperty = (await Database.get('dashboard-carriers-by')) || DEFAULT_CARRIER_BREAKDOWN_PROPERTY
  },
  methods: {
    async loadDashboardData () {
      this.isLoadingDashboardData = true

      this.dashboards = Dashboard.fields

      const response = await Dashboard.list(request => {
        const filters = {
          dateFrom: this.dateFrom.format('YYYY-MM-DD HH:mm:ss'),
          dateTo: this.dateTo.format('YYYY-MM-DD HH:mm:ss'),
          dateRange: this.dateRange,
          account: this.$route.params.accountId
        }

        if (this.$route.query.team) {
          filters.team = this.$route.query.team
        }

        request.filters = filters
      })

      if (response.length === 1) {
        this.dashboards = response[0]
      }

      this.isLoadingDashboardData = false
    },
    setTopReceiversProperty (property) {
      this.topReceiversProperty = property
    },
    setCarrierBreakdownProperty (property) {
      this.carrierBreakdownProperty = property
    }
  }
}
</script>
